import { useMemo, useState } from "react";
import { Alert, Anchor, Badge, Button, Group, Table, Text, TextInput, Title } from "@mantine/core";
import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";

import { loadBranchCatalog, loadCatalog } from "@/api/catalogs";
import { RequirePermission } from "@/auth/guards";
import { AppLayout } from "@/components/AppLayout";
import { TableSkeleton } from "@/components/TableSkeleton";
import { t } from "@/i18n";

/**
 * Phase 3 (ADR-0040 read surfaces) — one catalog's biomarkers.
 *
 * Serves both `/catalogs/:org` (tenant) and `/catalogs/:org/branch/:branch`
 * (branch) via `GET /api/v1/catalogs/{org}[/branch/{branch}]`. A catalog can
 * carry ~1,660 biomarkers, so a client-side name/display filter is part of the
 * slice. Backend enforces `catalog:read`; the route wraps `<Authenticated>` +
 * `<RequirePermission>` in App.tsx.
 */
export function CatalogDetailPage(): JSX.Element {
  const { org = "", branch } = useParams<{ org: string; branch?: string }>();
  const [filter, setFilter] = useState("");

  const catalogBase = branch
    ? `/catalogs/${encodeURIComponent(org)}/branch/${encodeURIComponent(branch)}`
    : `/catalogs/${encodeURIComponent(org)}`;

  const { data, isPending, isError, error } = useQuery({
    queryKey: branch ? ["catalog", org, "branch", branch] : ["catalog", org],
    queryFn: () => (branch ? loadBranchCatalog(org, branch) : loadCatalog(org)),
  });

  const filtered = useMemo(() => {
    if (data === undefined) return [];
    const q = filter.trim().toLowerCase();
    if (q === "") return data.biomarkers;
    return data.biomarkers.filter(
      (b) => b.name.toLowerCase().includes(q) || b.display.toLowerCase().includes(q),
    );
  }, [data, filter]);

  return (
    <AppLayout
      breadcrumbs={[
        { label: t("catalogs.title"), to: "/catalogs" },
        { label: branch ? `${org} / ${branch}` : org },
      ]}
    >
      <Group gap="sm" align="baseline" justify="space-between">
        <Group gap="sm" align="baseline">
          <Title order={1}>{branch ? `${org} / ${branch}` : org}</Title>
          {data !== undefined && <Badge variant="light">{data.tier}</Badge>}
        </Group>
        <Group gap="sm">
          {!branch && (
            <Button
              component={Link}
              to={`/catalogs/${encodeURIComponent(org)}/config`}
              variant="default"
              size="sm"
            >
              {t("catalog_detail.client_config")}
            </Button>
          )}
          <RequirePermission perm="catalog:author">
            <Button component={Link} to={`${catalogBase}/spreadsheet`} variant="default" size="sm">
              {t("catalog_detail.bulk_upload")}
            </Button>
            <Button component={Link} to={`${catalogBase}/new-biomarker`} size="sm">
              {t("catalog_detail.new_biomarker")}
            </Button>
          </RequirePermission>
        </Group>
      </Group>

      {isPending ? (
        <TableSkeleton label={t("catalog_detail.loading")} />
      ) : isError ? (
        <Alert color="red" title={t("catalog_detail.error_title")}>
          {error instanceof Error ? error.message : t("catalogs.error_generic")}
        </Alert>
      ) : (
        <>
          <TextInput
            placeholder={t("catalog_detail.filter_placeholder")}
            value={filter}
            onChange={(e) => setFilter(e.currentTarget.value)}
            aria-label={t("catalog_detail.filter_placeholder")}
            maw={360}
          />
          <Text size="sm" c="dimmed">
            {t("catalog_detail.count_prefix")} {filtered.length} / {data.biomarkers.length}
          </Text>
          {filtered.length === 0 ? (
            <Text c="dimmed">{t("catalog_detail.no_matches")}</Text>
          ) : (
            <Table striped highlightOnHover withTableBorder>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t("catalog_detail.col_symbol")}</Table.Th>
                  <Table.Th>{t("catalog_detail.col_display")}</Table.Th>
                  <Table.Th>{t("catalog_detail.col_abbrev")}</Table.Th>
                  <Table.Th>{t("catalog_detail.col_panel")}</Table.Th>
                  <Table.Th>{t("catalog_detail.col_type")}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {filtered.map((b) => (
                  <Table.Tr key={b.name}>
                    <Table.Td>
                      <Anchor
                        component={Link}
                        to={`${catalogBase}/biomarker/${encodeURIComponent(b.name)}`}
                        ff="monospace"
                        size="sm"
                      >
                        {b.name}
                      </Anchor>
                    </Table.Td>
                    <Table.Td>{b.display}</Table.Td>
                    <Table.Td>{b.abbreviation ?? "—"}</Table.Td>
                    <Table.Td>{b.panel_name ?? "—"}</Table.Td>
                    <Table.Td>
                      {b.members.length > 0 && (
                        <Badge color="grape" variant="light">
                          {t("catalog_detail.grouper")}
                        </Badge>
                      )}
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          )}
        </>
      )}
    </AppLayout>
  );
}
